import React, { useEffect, useState, useContext } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const StateList = ({ onEdit, refreshTrigger }) => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);
  const isPrimary = user?.is_primary;

  useEffect(() => {
    fetchStates();
  }, [refreshTrigger]);

  const fetchStates = async () => {
    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      const response = await secureAxios.get(`${apiBase}/api/states`);
      setStates(response.data);
    } catch (err) {
      console.error("❌ Error loading states:", err);
      setError(err.response?.data?.error || "Failed to load states");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, stateName, stateCode) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ${stateName} (${stateCode})?\n\nThis will affect ALL tenants and cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(`${apiBase}/api/states/${id}`);
      setStates((prev) => prev.filter((s) => s.state_id !== id));
      alert("✅ State deleted successfully!");
    } catch (err) {
      console.error("❌ Failed to delete state", err);
      alert(err.response?.data?.error || "Failed to delete state");
    }
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button
          className="btn btn-sm btn-outline-danger ms-3"
          onClick={fetchStates}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>US States & Territories</h4>
        <span className="badge bg-secondary">{states.length} states</span>
      </div>

      {/* Desktop/Tablet Table View */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col">State Name</th>
              <th scope="col">Code</th>
              <th scope="col">Last Updated</th>
              <th scope="col" className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {states.map((s) => (
              <tr key={s.state_id}>
                <td>
                  <strong>{s.state_name}</strong>
                </td>
                <td>
                  <span className="badge bg-primary">{s.state_code}</span>
                </td>
                <td>
                  <small className="text-muted">
                    {new Date(
                      s.updated_at || s.created_at
                    ).toLocaleDateString()}
                  </small>
                </td>
                <td className="text-end">
                  {isPrimary ? (
                    <>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => onEdit(s)}
                        title="Edit state"
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          handleDelete(s.state_id, s.state_name, s.state_code)
                        }
                        title="Delete state"
                      >
                        <i className="bi bi-trash"></i> Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-muted">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile-Friendly List View */}
      <div className="d-block d-md-none">
        {states.map((s) => (
          <div key={s.state_id} className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">
                {s.state_name}
                <span className="badge bg-primary ms-2">{s.state_code}</span>
              </h5>
              <p className="card-text">
                <small className="text-muted">
                  Last updated:{" "}
                  {new Date(s.updated_at || s.created_at).toLocaleDateString()}
                </small>
              </p>
              {isPrimary && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onEdit(s)}
                  >
                    <i className="bi bi-pencil"></i> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() =>
                      handleDelete(s.state_id, s.state_name, s.state_code)
                    }
                  >
                    <i className="bi bi-trash"></i> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {!isPrimary && (
        <div className="alert alert-info mt-3" role="alert">
          <i className="bi bi-info-circle"></i> Only primary tenant users can
          manage states.
        </div>
      )}
    </div>
  );
};

export default StateList;
